import type { MeResponse } from '@tabel/shared/schemas/auth';
import { type Direction, type SiteDetail, type SiteStep, Step2Answers } from '@tabel/shared/schemas/sites';
import { cityLabel } from '@tabel/shared/sites/cities';
import {
  COMMON,
  LINKS,
  OBSERVATION,
  STEP_BODY,
  STEP_TITLES,
  type StepView,
  formatDuration,
  launchReminder,
  localDate,
  localTime,
  stepReady,
  tzHint,
} from '@tabel/shared/sites/step-texts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  CircleAlert,
  CircleCheck,
  ClipboardList,
  Clock,
  Copy,
  Download,
  Eye,
  EyeOff,
  KeyRound,
  Smartphone,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyValue } from '../../../components/CopyValue';
import { UnsavedMark, useUnsavedChanges } from '../../../components/UnsavedGuard';
import { Button, Callout, LinkButton, ModeCards, fieldCls, inputCls } from '../../../components/ui';
import { reportClientError } from '../../../lib/errors';
import { localToday, ruDateTime } from '../../../lib/format';
import { NAV_PATH, SETTINGS_PATH } from '../../../lib/nav';
import { useRole } from '../../../lib/role';
import { rulePeople, ruleSources, sitesKeys } from '../api';
import { FormField, LoadError, SCREEN, shouldReport } from '../parts';
import { FORM, SITE } from '../texts';
import { FileButton, RecordsUpload } from './RecordsUpload';
import { StepHints } from './StepHints';
import { useSite } from './actions';
import {
  controlPresses,
  controlRows,
  dayMonth,
  localClock,
  observationFacts,
  observationItems,
  step2Answers,
  step7Times,
} from './model';

/*
 * Тела шагов страницы площадки (007 T040, T053; spec.md FR-017…FR-027; прототип prototypes/site-onboarding.html,
 * функция `body`; дизайн-система Steps: «внутри шага — что сделать, значения с кнопкой «Скопировать» и итог проверки
 * подсказкой `Callout`»). Тексты — только `@tabel/shared/sites/step-texts` и `../texts`; значения — из ответа сервера.
 * Закрытый шаг (`LOCKED`) читается, но кнопки и поля недоступны, и причина написана (FR-011). Всё, что нажимают на
 * телефоне, — цель не меньше 40 px.
 */

interface BodyProps {
  step: SiteStep;
  view: StepView;
}

/** Закрыт ли шаг для действий: закрыт по порядку шагов или площадка отключена. */
function useBlocked(step: SiteStep): boolean {
  const { detail } = useSite();
  return step.code === 'LOCKED' || detail.site.state === 'disabled';
}

function H4({ children }: { children: ReactNode }) {
  return <h4 className="text-[13px] font-semibold text-ink">{children}</h4>;
}

function Todo({ children }: { children: ReactNode }) {
  return (
    <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[14px] text-ink marker:text-ink-3 marker:tabular-nums">
      {children}
    </ol>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="text-[13px] text-ink-2">{children}</p>;
}

function Caption({ children }: { children: ReactNode }) {
  return <p className="text-[12px] text-ink-2">{children}</p>;
}

/** Итог проверки — подсказки шага с действиями-ссылками (T041). */
function Hints({ step, view }: BodyProps) {
  const { act, busy } = useSite();
  const blocked = useBlocked(step);
  return (
    <StepHints
      hints={view.hints}
      onAction={(a) => act.hint(a, step.step)}
      disabled={blocked || busy !== null}
    />
  );
}

/** Причина закрытого шага — сверху тела (у шага 10 — под кнопкой). */
function LockedNote({ view }: { view: StepView }) {
  return view.note ? <Note>{view.note}</Note> : null;
}

// ── Шаг 1 ──

export function Step1({ step, view }: BodyProps) {
  const { detail } = useSite();
  const s = detail.site;
  return (
    <>
      <dl className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <div className="min-w-0">
          <dt className="text-[13px] font-medium text-ink">{STEP_BODY[1].nameLabel}</dt>
          <dd className="mt-1 text-[14px] text-ink">{s.name}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[13px] font-medium text-ink">{STEP_BODY[1].cityLabel}</dt>
          <dd className="mt-1 text-[14px] text-ink">{cityLabel(s.city, s.tzOffset)}</dd>
          <dd className="mt-1 text-[12px] text-ink-3">{tzHint(s.tzOffset)}</dd>
        </div>
      </dl>
      <Hints step={step} view={view} />
    </>
  );
}

// ── Шаг 2 ──

type Step2Errors = Partial<Record<'terminals' | 'allDay' | 'keepDays', string>>;

export function Step2({ step, view }: BodyProps) {
  const saved = step2Answers(step);
  // Новые ответы с сервера — форма заново; опрос с теми же ответами форму не трогает.
  return (
    <>
      <LockedNote view={view} />
      <Note>{STEP_BODY[2].note}</Note>
      <Step2Form key={JSON.stringify(saved)} step={step} saved={saved} />
      <Hints step={step} view={view} />
    </>
  );
}

/** Поля формы шага 2 как введены: строки полей и выбор «круглосуточно». */
interface Step2Input {
  version: string;
  terminals: string;
  allDay: boolean | null;
  keepDays: string;
}
const step2Input = (saved: ReturnType<typeof step2Answers>): Step2Input => ({
  version: saved?.version ?? '',
  terminals: saved ? String(saved.terminals) : '',
  allDay: saved?.allDay ?? null,
  keepDays: saved ? String(saved.keepDays) : '',
});
const sameInput = (a: Step2Input, b: Step2Input) =>
  a.version.trim() === b.version.trim() &&
  a.terminals.trim() === b.terminals.trim() &&
  a.allDay === b.allDay &&
  a.keepDays.trim() === b.keepDays.trim();

function Step2Form({ step, saved }: { step: SiteStep; saved: ReturnType<typeof step2Answers> }) {
  const id = useId();
  const { act, busy } = useSite();
  const blocked = useBlocked(step);
  const [version, setVersion] = useState(saved?.version ?? '');
  const [terminals, setTerminals] = useState(saved ? String(saved.terminals) : '');
  const [allDay, setAllDay] = useState<boolean | null>(saved?.allDay ?? null);
  const [keepDays, setKeepDays] = useState(saved ? String(saved.keepDays) : '');
  const [errors, setErrors] = useState<Step2Errors>({});
  /** Что сохранено на сервере: ответы с сервера или последние сохранённые этой формой. */
  const [baseline, setBaseline] = useState<Step2Input>(() => step2Input(saved));
  const T = STEP_BODY[2];

  const input: Step2Input = { version, terminals, allDay, keepDays };
  const answers = {
    version: version.trim(),
    terminals: Number(terminals),
    allDay,
    keepDays: Number(keepDays),
  };
  const parsed = Step2Answers.safeParse(answers);
  const dirty = !blocked && !sameInput(input, baseline);

  /** Сохранить; не вышло — `false` (ошибку показал тост). */
  const save = async (data: { version: string; terminals: number; allDay: boolean; keepDays: number }) => {
    const done = await act.saveStep2(data);
    if (done) setBaseline(input);
    return done;
  };

  // Защита несохранённого (FR-008, ux.md §4): уход со страницы спрашивает «Сохранить ответы?».
  useUnsavedChanges(
    dirty
      ? {
          kind: 'answers',
          summary: FORM.answersUnsaved(STEP_TITLES[2]),
          canSave: parsed.success,
          save: async () => {
            if (!parsed.success || !(await save(parsed.data))) throw new Error('Ответы шага 2 не сохранены');
          },
        }
      : null,
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const err: Step2Errors = {};
    if (!parsed.success) {
      if (!/^\d+$/.test(terminals) || answers.terminals < 1 || answers.terminals > 100)
        err.terminals = FORM.terminals;
      if (allDay === null) err.allDay = FORM.required;
      if (!/^\d+$/.test(keepDays) || answers.keepDays > 3650) err.keepDays = FORM.keepDays;
    }
    setErrors(err);
    if (!parsed.success || Object.keys(err).length > 0) return;
    void save(parsed.data);
  };

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <FormField id={`${id}-v`} label={T.version.label} hint={T.version.hint}>
          {(a) => (
            <input
              {...a}
              className={`${inputCls} font-mono`}
              value={version}
              maxLength={40}
              disabled={blocked}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setVersion(e.target.value)}
            />
          )}
        </FormField>
        <FormField id={`${id}-t`} label={T.terminals.label} hint={T.terminals.hint} error={errors.terminals}>
          {(a) => (
            <input
              {...a}
              className={`${inputCls} tabular-nums`}
              value={terminals}
              inputMode="numeric"
              disabled={blocked}
              onChange={(e) => setTerminals(e.target.value)}
            />
          )}
        </FormField>
        <fieldset className="flex min-w-0 flex-col gap-1.5" disabled={blocked}>
          <legend className="mb-1.5 text-[13px] font-medium text-ink">{T.allDay.label}</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {([true, false] as const).map((v) => (
              <label
                key={String(v)}
                className="inline-flex min-h-9 cursor-pointer items-center gap-2 text-[14px] max-sm:min-h-10"
              >
                <input
                  type="radio"
                  className="t-radio"
                  name={`${id}-allday`}
                  checked={allDay === v}
                  onChange={() => setAllDay(v)}
                />
                {v ? T.allDay.yes : T.allDay.no}
              </label>
            ))}
          </div>
          {errors.allDay && <span className="text-[12px] text-bad">{errors.allDay}</span>}
        </fieldset>
        <FormField id={`${id}-k`} label={T.keepDays.label} hint={T.keepDays.hint} error={errors.keepDays}>
          {(a) => (
            <input
              {...a}
              className={`${inputCls} tabular-nums`}
              value={keepDays}
              inputMode="numeric"
              disabled={blocked}
              onChange={(e) => setKeepDays(e.target.value)}
            />
          )}
        </FormField>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={blocked || busy !== null}>
          {FORM.saveAnswers}
        </Button>
        {dirty && <UnsavedMark />}
      </div>
    </form>
  );
}

// ── Шаг 3 ──

export function Step3({ step, view }: BodyProps) {
  const { detail, act, busy, setDialog } = useSite();
  const blocked = useBlocked(step);
  const T = STEP_BODY[3];
  const mode = detail.site.linkMode;
  const vpn = mode === 'vpn';
  const configAvailable = detail.vpn?.configAvailable ?? false;
  return (
    <>
      <LockedNote view={view} />
      <Note>{T.note}</Note>
      <ModeCards
        label={STEP_TITLES[3]}
        name={`link-${detail.site.code}`}
        value={mode}
        disabled={blocked || busy !== null}
        onChange={act.changeLink}
        options={[
          { value: 'direct', title: T.modes.direct.title, lines: [T.modes.direct.need, T.modes.direct.get] },
          { value: 'vpn', title: T.modes.vpn.title, lines: [T.modes.vpn.need, T.modes.vpn.get] },
        ]}
      />
      {vpn && (
        <>
          <H4>{COMMON.todoTitle}</H4>
          <Todo>
            <li>
              {T.todo[0]}{' '}
              <a
                href={LINKS.wireguard}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-accent underline-offset-2 hover:underline max-sm:min-h-10"
              >
                wireguard.com/install
                <ArrowUpRight size={14} strokeWidth={1.75} aria-hidden />
              </a>
            </li>
            <li>
              {T.todo[1]}
              <div className="mt-1.5">
                {configAvailable ? (
                  <Button
                    icon={Download}
                    disabled={blocked || busy !== null}
                    onClick={() => void act.download('wireguard')}
                  >
                    {T.download}
                  </Button>
                ) : (
                  <Button
                    icon={KeyRound}
                    disabled={detail.site.state === 'disabled' || busy !== null}
                    onClick={() => setDialog({ kind: 'reissue', what: 'vpn_key' })}
                  >
                    {T.reissue}
                  </Button>
                )}
              </div>
            </li>
            <li>{T.todo[2]}</li>
            <li>{T.todo[3]}</li>
          </Todo>
          <H4>{COMMON.checkTitle}</H4>
        </>
      )}
      <Hints step={step} view={view} />
      {vpn && <Caption>{T.caption}</Caption>}
    </>
  );
}

// ── Шаг 4 ──

export function Step4({ step, view }: BodyProps) {
  const { detail, act, busy, password, setDialog } = useSite();
  const blocked = useBlocked(step);
  const T = STEP_BODY[4];
  const v = detail.smartpss;
  const rows: [string, string][] = [
    [T.values.dbType, v.dbType],
    [T.values.serverIp, v.serverIp],
    [T.values.port, String(v.port)],
    [T.values.dbName, v.dbName],
    [T.values.user, v.user],
  ];
  const shown = password.shown && password.value !== null;
  return (
    <>
      <LockedNote view={view} />
      <H4>{COMMON.todoTitle}</H4>
      <Todo>
        {T.todo.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </Todo>
      <div className="rounded-control border border-line">
        {rows.map(([label, value]) => (
          <ValueRow key={label} label={label}>
            <CopyValue value={value} label={label} />
          </ValueRow>
        ))}
        <ValueRow label={T.values.password}>
          <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
            <span className="min-w-0 font-mono text-[13px] [overflow-wrap:anywhere] text-ink">
              {shown ? password.value : SITE.mask}
            </span>
            {v.passwordAvailable && (
              <>
                <Button
                  variant="ghost"
                  icon={shown ? EyeOff : Eye}
                  aria-label={shown ? T.hidePassword : T.showPassword}
                  disabled={blocked || busy !== null}
                  onClick={() => (shown ? act.hidePassword() : void act.showPassword())}
                >
                  <span className="max-sm:sr-only">{shown ? T.hide : T.show}</span>
                </Button>
                <Button
                  variant="ghost"
                  icon={Copy}
                  aria-label={`${COMMON.copy}: ${T.values.password}`}
                  disabled={blocked || busy !== null}
                  onClick={() => void act.copyPassword()}
                >
                  <span className="max-sm:sr-only">{COMMON.copy}</span>
                </Button>
              </>
            )}
          </span>
        </ValueRow>
      </div>
      <SmartpssScheme />
      <H4>{COMMON.checkTitle}</H4>
      <Hints step={step} view={view} />
      <Caption>{T.caption}</Caption>
      {!v.passwordAvailable && step.code !== 'LOCKED' && (
        <div>
          <Button
            icon={KeyRound}
            disabled={detail.site.state === 'disabled' || busy !== null}
            onClick={() => setDialog({ kind: 'reissue', what: 'password' })}
          >
            {T.reissue}
          </Button>
        </div>
      )}
    </>
  );
}

/** Строка значений для SmartPSS: подпись слева, значение и кнопки справа; на телефоне — подпись над значением. */
function ValueRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-h-10 grid-cols-[150px_minmax(0,1fr)] items-center gap-3 border-b border-line py-1 pr-1.5 pl-3 last:border-b-0 max-sm:grid-cols-1 max-sm:gap-0 max-sm:py-2">
      <span className="text-[13px] text-ink-2">{label}</span>
      {children}
    </div>
  );
}

/** Упрощённая схема окна SmartPSS «База данных посещаемости (сторонняя)» (FR-020): поля в том же порядке. */
function SmartpssScheme() {
  const { detail } = useSite();
  const T = STEP_BODY[4];
  const v = detail.smartpss;
  const fields: [string, string][] = [
    [T.values.serverIp, v.serverIp],
    [T.values.port, String(v.port)],
    [T.values.dbName, v.dbName],
    [T.values.user, v.user],
    [T.values.password, '••••••••••••'],
  ];
  return (
    <div className="grid max-w-[460px] grid-cols-[140px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-control border border-line bg-subtle p-3 text-[12px] max-sm:grid-cols-1">
      <span className="col-span-full font-semibold text-ink-2 max-sm:col-span-1">{T.schemeTitle}</span>
      <div aria-hidden className="contents">
        <span>{T.values.dbType}</span>
        <SchemeBox>{v.dbType}</SchemeBox>
        {fields.map(([label, value], i) => (
          <SchemeField key={label} n={i + 1} label={label} value={value} />
        ))}
        <span className="col-span-full flex flex-wrap gap-2 max-sm:col-span-1">
          <span className="inline-flex h-[26px] items-center rounded-[6px] border border-line-strong bg-surface px-2.5">
            {T.testButton}
          </span>
          <span className="inline-flex h-[26px] items-center rounded-[6px] border border-line-strong bg-surface px-2.5">
            {T.enableButton}
          </span>
        </span>
      </div>
    </div>
  );
}

function SchemeField({ n, label, value }: { n: number; label: string; value: string }) {
  return (
    <>
      <span className="inline-flex items-center">
        <span className="mr-1.5 inline-grid h-[18px] w-[18px] place-items-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent">
          {n}
        </span>
        {label}
      </span>
      <SchemeBox>{value}</SchemeBox>
    </>
  );
}

function SchemeBox({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-[26px] min-w-0 items-center overflow-hidden rounded-[6px] border border-line-strong bg-surface px-2 font-mono text-ellipsis whitespace-nowrap">
      {children}
    </span>
  );
}

// ── Шаг 5 ──

const DIRECTIONS: readonly Direction[] = ['IN', 'OUT', 'SKIP'];

export function Step5({ step, view }: BodyProps) {
  const { detail, act, busy } = useSite();
  const blocked = useBlocked(step);
  const T = STEP_BODY[5];
  const tz = detail.site.tzOffset;
  const confirmed = step.details.manualDirectionsConfirmed === true;
  const canConfirm =
    detail.terminals.length > 0 &&
    detail.terminals.every(
      (t) =>
        t.direction !== null &&
        (t.direction === 'SKIP' || t.problem === null || t.problem === 'NO_DIRECTION'),
    );
  return (
    <>
      <LockedNote view={view} />
      <H4>{COMMON.todoTitle}</H4>
      <Todo>
        {T.todo.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </Todo>
      <Caption>{T.caption}</Caption>
      {detail.terminals.length > 0 && (
        <div className="overflow-x-auto rounded-control border border-line">
          <table className="t-tbl t-tbl-stack" aria-label={STEP_TITLES[5]}>
            <thead>
              <tr>
                <th className="sticky">{T.columns.terminal}</th>
                <th className="num">{T.columns.count}</th>
                <th>{T.columns.last}</th>
                <th>{T.columns.direction}</th>
                <th>{T.columns.check}</th>
              </tr>
            </thead>
            <tbody>
              {detail.terminals.map((t) => {
                const check =
                  t.direction === 'SKIP'
                    ? { text: T.check.skip, cls: 'text-ink-3' }
                    : t.problem
                      ? { text: T.check.bad, cls: 'text-bad' }
                      : t.direction === null
                        ? { text: '—', cls: 'text-ink-3' }
                        : { text: T.check.ok, cls: 'text-ok' };
                return (
                  <tr key={t.ip}>
                    <td className="sticky">
                      <span className="font-mono">{t.ip}</span>
                      <span className="block text-[12px] font-normal whitespace-normal text-ink-2">
                        {T.deviceName} {t.deviceName ?? '—'}
                      </span>
                    </td>
                    <td className="num">
                      <span className="sm:hidden">{T.columns.count}: </span>
                      {t.count}
                    </td>
                    {/* С датой (ux.md «Таблица»): вчерашнее «14:02» без даты читают как сегодняшнее. */}
                    <td className="t-num">
                      <span className="sm:hidden">{T.columns.last}: </span>
                      {ruDateTime(t.lastPassAt, tz)}
                    </td>
                    <td>
                      <select
                        aria-label={`${T.columns.direction}: ${t.ip}`}
                        className={fieldCls}
                        value={t.direction ?? ''}
                        disabled={blocked || busy !== null}
                        onChange={(e) => void act.setDirection(t.ip, e.target.value as Direction)}
                      >
                        {t.direction === null && (
                          <option value="" disabled>
                            {FORM.direction}
                          </option>
                        )}
                        {DIRECTIONS.map((d) => (
                          <option key={d} value={d}>
                            {T.directions[d]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={check.cls}>{check.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {confirmed ? (
        <Note>{T.manualDirections.confirmed}</Note>
      ) : step.code === 'NO_DIRECTION' ? (
        <div className="flex flex-col items-start gap-2">
          <Note>{T.manualDirections.note}</Note>
          <Button
            disabled={blocked || busy !== null || !canConfirm}
            onClick={() => void act.confirmDirections()}
          >
            {T.manualDirections.confirm}
          </Button>
        </div>
      ) : null}
      <Hints step={step} view={view} />
    </>
  );
}

// ── Шаг 6 ──

export function Step6({ step, view }: BodyProps) {
  const { detail, act, busy } = useSite();
  const blocked = useBlocked(step);
  const T = STEP_BODY[6];
  const tz = detail.site.tzOffset;
  const { inAt } = controlPresses(step);
  const rows = controlRows(step);
  const clockKind = step.code === 'CTL_CLOCK' ? step.details.kind : null;
  const link = `${window.location.origin}${SETTINGS_PATH.site(detail.site.code)}#step6`;
  return (
    <>
      <LockedNote view={view} />
      <H4>{COMMON.todoTitle}</H4>
      <Todo>
        <li>
          {T.todo[0]}
          <div className="mt-1.5">
            <Button icon={Smartphone} onClick={() => act.copyValue(T.copyLink, link)}>
              {T.copyLink}
            </Button>
          </div>
        </li>
        <li>{T.todo[1]}</li>
        <li>{T.todo[2]}</li>
      </Todo>
      <Note>{T.optionalNote}</Note>
      <div className="flex flex-wrap gap-2 max-sm:[&>button]:flex-auto">
        <Button size="lg" disabled={blocked || busy !== null} onClick={() => void act.pass('in')}>
          {T.passedIn}
        </Button>
        <Button size="lg" disabled={blocked || busy !== null || !inAt} onClick={() => void act.pass('out')}>
          {T.passedOut}
        </Button>
        {step.code !== 'CTL_SKIPPED' && step.code !== 'CTL_OK' && (
          <Button size="lg" disabled={blocked || busy !== null} onClick={() => void act.skipControl()}>
            {T.skip}
          </Button>
        )}
      </div>
      {!inAt && !blocked && <Caption>{T.outAfterIn}</Caption>}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-control border border-line">
          <table className="t-tbl t-tbl-stack" aria-label={STEP_TITLES[6]}>
            <thead>
              <tr>
                <th className="sticky">{T.columns.pressed}</th>
                <th>{T.columns.pass}</th>
                <th>{T.columns.terminal}</th>
                <th className="num">{T.columns.delay}</th>
                <th>{T.columns.result}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const want = r.kind === 'in' ? 'IN' : 'OUT';
                const result =
                  r.passAt === null
                    ? { text: T.results.lost, cls: 'text-bad' }
                    : r.direction !== want
                      ? { text: T.results.wrong, cls: 'text-bad' }
                      : clockKind === r.kind
                        ? { text: STEP_BODY[5].check.bad, cls: 'text-bad' }
                        : { text: T.results.ok, cls: 'text-ok' };
                return (
                  <tr key={r.kind}>
                    <td className="sticky t-num">
                      <span className="sm:hidden">{T.columns.pressed}: </span>
                      {localClock(r.pressedAt, tz)}
                    </td>
                    <td className={r.passAt ? 't-num' : 'text-ink-3'}>
                      <span className="sm:hidden">{T.columns.pass}: </span>
                      {r.passAt ? localClock(r.passAt, tz) : T.notFound}
                    </td>
                    <td className={r.ip ? '' : 'text-ink-3'}>
                      {r.ip
                        ? `${r.ip}${r.direction ? ` · ${STEP_BODY[5].directions[r.direction]}` : ''}`
                        : '—'}
                    </td>
                    <td className="num">
                      <span className="sm:hidden">{T.columns.delay}: </span>
                      {r.delaySec === null ? '—' : formatDuration(r.delaySec)}
                    </td>
                    <td className={result.cls}>{result.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Hints step={step} view={view} />
    </>
  );
}

// ── Шаг 7 ──

export function Step7({ step, view }: BodyProps) {
  const { act, busy } = useSite();
  const blocked = useBlocked(step);
  const T = STEP_BODY[7];
  const times = step7Times(step, T.defaultTimes);
  return (
    <>
      <LockedNote view={view} />
      <H4>{COMMON.todoTitle}</H4>
      <Note>{T.place}</Note>
      <Todo>
        {T.todo(times).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </Todo>
      {step.code === 'EXTRACT_TODO' && (
        <div>
          <Button disabled={blocked || busy !== null} onClick={() => void act.saveStep7(times)}>
            {T.done}
          </Button>
        </div>
      )}
      {step.code === 'EXTRACT_WAIT' && step.details.manual === true && (
        <div>
          <Button disabled={blocked || busy !== null} onClick={() => void act.confirmExtract()}>
            {T.confirm}
          </Button>
        </div>
      )}
      <Hints step={step} view={view} />
    </>
  );
}

// ── Шаг 8 (T053) ──

export function Step8({ step, view }: BodyProps) {
  const id = useId();
  const { act, busy } = useSite();
  const blocked = useBlocked(step);
  const T = STEP_BODY[8];
  const off = blocked || busy !== null;
  return (
    <>
      <LockedNote view={view} />
      <H4>{COMMON.todoTitle}</H4>
      <Todo>
        {T.todo.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </Todo>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <FileButton
          id={`${id}-file`}
          label={T.upload}
          disabled={off}
          onFile={(file) => void act.upload('people', file)}
        />
        <span id={`${id}-file-note`} className="text-[12px] text-ink-2">
          {T.uploadHint}
        </span>
      </div>
      <Hints step={step} view={view} />
    </>
  );
}

// ── Шаг 9 ──

type ItemState = 'ok' | 'wait' | 'bad';
const ITEM_ICON = { ok: CircleCheck, wait: Clock, bad: CircleAlert } as const;
const ITEM_CLS = { ok: 'text-ok', wait: 'text-ink-3', bad: 'text-bad' } as const;
const ITEM_WORD = { ok: FORM.itemDone, wait: FORM.itemWait, bad: FORM.itemBad } as const;

export function Step9({ step, view }: BodyProps) {
  const { detail } = useSite();
  const items = observationItems(step);
  const facts = observationFacts(step);
  const O = OBSERVATION;
  const tz = detail.site.tzOffset;
  /** «21.10» и «05:52» по поясу площадки. */
  const dm = (iso: string) => localDate(iso, tz).slice(0, 5);
  const flag = (v: boolean): ItemState => (v ? 'ok' : 'wait');
  const problem = step.code === 'OBS_DELETES' || step.code === 'OBS_CODES';
  // Строки пунктов — по прототипу (`viewSite`, шаг 9): выполнен — факт из деталей сервера, нет — «Сколько ждать».
  const night = items.night
    ? facts.firstPassAt && O.firstPass(dm(facts.firstPassAt), localTime(facts.firstPassAt, tz))
    : O.items.night.pending;
  const extract = items.extract
    ? facts.lastExtract
      ? O.extractCame(dm(facts.lastExtract.at), localTime(facts.lastExtract.at, tz), facts.lastExtract.rows)
      : facts.extractManualAt && O.extractManual(dm(facts.extractManualAt))
    : O.items.extract.pending;
  const terminals =
    facts.terminalsTotal > 0 && (items.terminals || facts.terminalsSeen > 0)
      ? O.terminalsSeen(facts.terminalsSeen, facts.terminalsTotal)
      : items.terminals
        ? null
        : O.items.terminals.pending;
  const left = 3 - items.days;
  const clean =
    problem || !items.clean
      ? null
      : step.code !== 'OBSERVED' && left > 0
        ? O.cleanLeft(left)
        : O.cleanDays(Math.max(1, items.days));
  const list: { key: string; state: ItemState; title: string; sub?: string }[] = [
    { key: 'night', state: flag(items.night), title: O.items.night.title, sub: night || undefined },
    { key: 'extract', state: flag(items.extract), title: O.items.extract.title, sub: extract || undefined },
    {
      key: 'terminals',
      state: flag(items.terminals),
      title: O.items.terminals.title,
      sub: terminals ?? undefined,
    },
    {
      key: 'clean',
      state: problem ? 'bad' : flag(items.clean),
      title: O.items.clean.title,
      sub: clean ?? undefined,
    },
  ];
  const days = detail.observation?.days ?? [];
  return (
    <>
      <LockedNote view={view} />
      <Note>{O.note}</Note>
      <ul aria-label={STEP_TITLES[9]} className="rounded-control border border-line">
        {list.map((it) => {
          const Icon = ITEM_ICON[it.state];
          return (
            <li
              key={it.key}
              className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-2.5 border-b border-line px-3 py-2.5 text-[13px] last:border-b-0"
            >
              <Icon size={16} strokeWidth={1.75} className={`mt-0.5 ${ITEM_CLS[it.state]}`} aria-hidden />
              <div className="min-w-0">
                <div className="text-ink">
                  {it.title}
                  <span className="sr-only"> — {ITEM_WORD[it.state]}</span>
                </div>
                {it.sub && <div className="text-[12px] text-ink-2">{it.sub}</div>}
              </div>
            </li>
          );
        })}
      </ul>
      {days.length > 0 && (
        <>
          <H4>{O.daysTitle}</H4>
          <ul aria-label={O.daysTitle} className="flex flex-col gap-1 text-[13px] text-ink-2">
            {days.map((d) => (
              <li key={d.date} className="tabular-nums">
                {O.day({ date: dayMonth(d.date), passes: d.passes, quiet: d.quiet, extracts: d.extracts })}
              </li>
            ))}
          </ul>
        </>
      )}
      <Hints step={step} view={view} />
    </>
  );
}

// ── Шаг 10 ──

/** Поле файла выгрузки записей шага 10: на него ведёт «Загрузить выгрузку» тревоги, пока площадка не запущена. */
export const STEP10_FILE_ID = 'site-step10-file';

export function Step10({ step, view }: BodyProps) {
  const id = useId();
  const { detail, act, busy } = useSite();
  const { role, setRole } = useRole();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const T = STEP_BODY[10];
  const launched = step.code === 'LAUNCHED';
  const ready = step.code === 'LAUNCH_READY' && detail.site.state !== 'disabled';
  // Правила к запуску (FR-004a): образец и тех, кто ведёт правила, выбирают и до готовности шагов.
  const open = !launched && detail.site.state !== 'disabled';
  const prep = detail.rulesPrep ?? null;
  const sites = useQuery({
    queryKey: sitesKeys.ruleSources(role, detail.site.code),
    queryFn: () => ruleSources(detail.site.code),
    enabled: open,
  });
  useEffect(() => {
    if (sites.error && shouldReport(sites.error)) reportClientError(SCREEN, 'rule-sources', sites.error);
  }, [sites.error]);
  // Образец правил — площадка с опубликованными правилами: подключённая к табелям (FR-004, research R14).
  const sources = sites.data ?? [];
  const [startDate, setStartDate] = useState(() => detail.site.startDate ?? localToday(detail.site.tzOffset));
  const [source, setSource] = useState(detail.site.rulesSource ?? '');
  useEffect(() => {
    if (source || sources.length === 0) return;
    setSource(sources.find((s) => s.code === 'sakh')?.code ?? sources[0]?.code ?? '');
  }, [source, sources]);
  // Черновик к запуску уже есть — образец не меняется (сервер тоже не даст).
  const sourceLocked = prep?.draft != null;
  const validSource = sources.some((s) => s.code === source);
  const people = useQuery({
    queryKey: sitesKeys.rulePeople(role, detail.site.code, source),
    queryFn: () => rulePeople(detail.site.code, source),
    enabled: open && validSource,
  });
  useEffect(() => {
    if (people.error && shouldReport(people.error)) reportClientError(SCREEN, 'rule-people', people.error);
  }, [people.error]);
  // Кто ведёт правила: по умолчанию — администраторы правил образца; кто уже ведёт — отмечен и не снимается.
  const [picked, setPicked] = useState<ReadonlySet<number> | null>(null);
  const list = people.data ?? [];
  const current = list.filter((p) => p.current).map((p) => p.userId);
  const chosen =
    picked ?? new Set(current.length > 0 ? current : list.filter((p) => p.sourceAdmin).map((p) => p.userId));
  const toggle = (userId: number) => {
    const next = new Set(chosen);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setPicked(next);
  };
  const admins = [...chosen];
  const hasAdmins = admins.length > 0 || (prep?.admins.length ?? 0) > 0;
  /** «Открыть правила»: страница правил площадки — ролью «Администратор правил», если она есть, иначе своей. */
  const openRules = () => {
    const me = qc.getQueriesData<MeResponse>({ queryKey: ['me'] }).find(([, d]) => d)?.[1];
    const roles = me?.objects?.find((o) => o.id === detail.site.code)?.roles ?? [];
    setRole(roles.includes('rules_admin') ? 'rules_admin' : role, detail.site.code);
    navigate(NAV_PATH.rules);
  };
  const remindPeople = detail.steps.find((s) => s.step === 8);
  const remind = ready && !!remindPeople && !stepReady(remindPeople);
  return (
    <>
      {!launched && (
        <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <FormField id={`${id}-start`} label={T.startLabel} hint={T.startHint}>
            {(a) => (
              <input
                {...a}
                type="date"
                className={inputCls}
                value={startDate}
                disabled={!ready}
                onChange={(e) => setStartDate(e.target.value)}
              />
            )}
          </FormField>
          <FormField
            id={`${id}-rules`}
            label={T.rulesLabel}
            hint={sourceLocked ? T.sourceLocked : T.rulesHint}
          >
            {(a) => (
              <select
                {...a}
                className={inputCls}
                value={source}
                disabled={!open || sources.length === 0 || sourceLocked}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPicked(null);
                }}
              >
                {sources.length === 0 && <option value="">{FORM.rulesSource}</option>}
                {sources.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </FormField>
        </div>
      )}
      {open && validSource && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-[13px] font-medium text-ink">{T.adminsLabel}</legend>
          {people.isError ? (
            <LoadError
              title={T.adminsError}
              error={people.error}
              busy={people.isFetching}
              onRetry={() => void people.refetch()}
            />
          ) : (
            <ul className="flex flex-col">
              {list.map((p) => (
                <li key={p.userId}>
                  <label className="inline-flex min-h-9 cursor-pointer items-center gap-2 text-[13px] text-ink max-sm:min-h-10">
                    <input
                      type="checkbox"
                      className="t-check"
                      checked={p.current || chosen.has(p.userId)}
                      disabled={p.current || busy !== null}
                      onChange={() => toggle(p.userId)}
                    />
                    <span>
                      {p.name}
                      {(p.current || p.sourceAdmin) && (
                        <span className="text-ink-3"> · {p.current ? T.adminsCurrent : T.adminsSource}</span>
                      )}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <Caption>{hasAdmins ? T.adminsHint : T.adminsEmpty}</Caption>
        </fieldset>
      )}
      {open && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <Button
            disabled={busy !== null || !validSource || admins.length === 0}
            onClick={() => void act.prepareRules(source, admins).then((ok) => ok && setPicked(null))}
          >
            {T.prepare}
          </Button>
          {!prep && <Caption>{T.prepareHint}</Caption>}
        </div>
      )}
      {open && prep && <PreparedRules prep={prep} onOpen={openRules} />}
      {!launched && (
        // Отметки за прошлые дни — по желанию (FR-027, FR-041; прототип — поле под датой начала): предпросмотр →
        // «Добавить». Главное действие шага — «Подключить к табелям», поэтому «Добавить» здесь вторичная. После запуска
        // загрузка — в «Ремонте».
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-ink">{T.historyLabel}</span>
          <RecordsUpload inputId={STEP10_FILE_ID} disabled={!ready} />
        </div>
      )}
      {remind && <StepHints hints={[launchReminder()]} onAction={(a) => act.hint(a, step.step)} />}
      {!launched &&
        (sites.isError ? (
          <LoadError
            title={FORM.rulesSourceError}
            error={sites.error}
            busy={sites.isFetching}
            onRetry={() => void sites.refetch()}
          />
        ) : null)}
      {open && sites.isSuccess && sources.length === 0 && <Caption>{FORM.rulesSourceEmpty}</Caption>}
      {!launched && (
        <div>
          <Button
            variant="primary"
            disabled={!ready || busy !== null || !startDate || !validSource || !hasAdmins}
            onClick={() => void act.launch(startDate, source, admins)}
          >
            {T.launch}
          </Button>
        </div>
      )}
      {view.note && <Caption>{view.note}</Caption>}
      <Hints step={step} view={view} />
    </>
  );
}

/**
 * Правила к запуску (FR-004a): итог — подсказкой в одну-две строки с действием-ссылкой в конце (дизайн-система Steps,
 * Callout); образец изменился после начала черновика — отдельная подсказка `warn`. Кто ведёт правила — в списке выше.
 */
function PreparedRules({ prep, onOpen }: { prep: NonNullable<SiteDetail['rulesPrep']>; onOpen: () => void }) {
  const T = STEP_BODY[10];
  return (
    <>
      <Callout tone="accent" icon={ClipboardList}>
        <b className="font-semibold">{T.preparedTitle}</b>{' '}
        {T.prepared(prep.sourceName, prep.draft?.changes ?? 0)}{' '}
        <LinkButton onClick={onOpen}>{T.openRules}</LinkButton>
      </Callout>
      {prep.sourceChanged && (
        <Callout tone="warn" icon={CircleAlert}>
          {T.sourceChanged(prep.sourceName)}
        </Callout>
      )}
    </>
  );
}
